/**
 * TNCY-5 — small fetch helper for the `tenancy/v1` namespace.
 *
 * Mirrors `projectsFetch.ts` / `assetsFetch.ts` — base URL comes from
 * `getTenancyBaseUrl()` so `DEV_API_MODE` (realbe / devapi-*) routes
 * requests correctly. Errors carry the parsed JSON body so the UI can
 * surface the BE error envelopes (`code`, `reason`, `message`).
 */
import { getTenancyBaseUrl, useAuthStore } from "@aeriosiq/auth-client";

export interface TenancyFetchError extends Error {
  status: number;
  body: unknown;
}

interface TenancyFetchOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
}

function authHeaders(): Headers {
  const session = useAuthStore.getState().session;
  const tenant = useAuthStore.getState().tenant;
  const headers = new Headers();
  if (session?.accessToken) {
    headers.set("Authorization", `Bearer ${session.accessToken}`);
  }
  if (tenant?.tenantSlug) {
    headers.set("X-Tenant-ID", tenant.tenantSlug);
  }
  return headers;
}

export async function tenancyFetch<T>(
  path: string,
  opts: TenancyFetchOptions = {},
): Promise<T> {
  const headers = authHeaders();
  let bodyInit: BodyInit | undefined;
  if (opts.body !== undefined) {
    bodyInit = JSON.stringify(opts.body);
    headers.set("Content-Type", "application/json");
  }
  const init: RequestInit = {
    method: opts.method ?? "GET",
    headers,
  };
  if (bodyInit !== undefined) {
    init.body = bodyInit;
  }
  const res = await fetch(`${getTenancyBaseUrl()}${path}`, init);
  if (!res.ok) {
    let parsedBody: unknown = null;
    try {
      parsedBody = await res.json();
    } catch {
      // Non-JSON error body — status still surfaces.
    }
    const err = new Error(
      `tenancy fetch failed: ${path} → ${String(res.status)}`,
    ) as TenancyFetchError;
    err.status = res.status;
    err.body = parsedBody;
    throw err;
  }
  if (res.status === 204) {
    return undefined as unknown as T;
  }
  const text = await res.text();
  if (text === "") return undefined as unknown as T;
  try {
    return JSON.parse(text) as T;
  } catch {
    return undefined as unknown as T;
  }
}
